/**
 * ExpectedSources — "what this document drafts from", the body of the
 * sources screen (new document, step 3), shown before the library list. It lists the
 * source documents the doctype declares (``expected_sources`` in its
 * manifest, served by ``GET /doc-types/{code}/expected-sources``), split into
 * the ones most sections use and the ones some sections use, and then says
 * plainly what happens when some are missing.
 *
 * # DECISION: reassure in the same breath as the list, and say only what the
 * #   product does today.
 * # WHY: a writer new to regulatory writing reads a list of eight documents as
 * #      eight prerequisites. They are not: the drafter writes a
 * #      ``[MISSING INFO: …]`` flag where a fact is absent (goal_driven
 * #      prompt), a source can be added to the library at any time, and Draft
 * #      more redrafts chosen sections against the library as it is then.
 * # DATE: 2026-10-21
 *
 * SRP: pure presentation. The picker fetches the list and owns the step.
 */

import type React from "react";
import type { ExpectedSource } from "../../api/client";

export interface ExpectedSourcesProps {
  /** ``null`` while loading. */
  sources: readonly ExpectedSource[] | null;
  /** The doctype's name, for "CSR usually draws on these". */
  docName?: string | null;
}

/** Split into the documents most sections use and the rest, keeping order. Pure. */
export function groupSources(sources: readonly ExpectedSource[]): {
  core: ExpectedSource[];
  helpful: ExpectedSource[];
} {
  return {
    core: sources.filter((s) => s.core),
    helpful: sources.filter((s) => !s.core),
  };
}

const SourceList: React.FC<{ label: string; sources: readonly ExpectedSource[] }> = ({ label, sources }) => {
  if (sources.length === 0) return null;
  return (
    <section aria-label={label} className="as-stack-8">
      <span className="as-eyebrow">{label}</span>
      <ul className="as-xsrc-list">
        {sources.map((source) => (
          <li key={source.name}>
            <span className="as-xsrc-name">{source.name}</span>
            {source.used_for && <span className="as-xsrc-for">{source.used_for}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const ExpectedSources: React.FC<ExpectedSourcesProps> = ({ sources, docName }) => {
  if (sources === null) {
    return <p className="as-note">Loading the usual sources…</p>;
  }
  const { core, helpful } = groupSources(sources);
  return (
    <div className="as-stack">
      <p className="as-p">
        Drafts come from the documents in your library. {docName || "This document"} usually draws on these.
      </p>
      <SourceList label="Most sections use" sources={core} />
      <SourceList label="Some sections use" sources={helpful} />
      <div className="as-notice as-notice-info" role="note">
        <b>Missing some? You can still start.</b>
        <div>
          Every section you pick is drafted. Where a source is missing, it leaves a{" "}
          <span className="as-xsrc-flag">[MISSING INFO: …]</span> flag in the text instead of guessing. Add the source to
          your library whenever you have it, then use Draft more to redraft the sections it affects.
        </div>
      </div>
    </div>
  );
};

export default ExpectedSources;
